import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Select, 
  DatePicker, 
  message,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Input,
} from 'antd';
import { 
  PlusOutlined, 
  ReloadOutlined,
  TeamOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import request from '../../../utils/request';

const { Option } = Select;

interface DataAssignment {
  id: number;
  user: number;
  user_username: string;
  user_email: string;
  resource_type: string;
  resource_type_display: string;
  resource_id: number;
  assigned_by: number;
  assigned_by_username: string;
  assigned_at: string;
  expires_at?: string;
  is_active: boolean;
}

const DataAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<DataAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [userOptions, setUserOptions] = useState<{ label: string; value: number }[]>([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const USERS_PAGE_SIZE = 10;
  const [resourceOptions, setResourceOptions] = useState<{ label: string; value: number }[]>([]);
  const [fetchingResources, setFetchingResources] = useState(false);
  const [resourceSearch, setResourceSearch] = useState('');
  const [resourcesPage, setResourcesPage] = useState(1);
  const [resourcesHasMore, setResourcesHasMore] = useState(false);
  // 资源显示名缓存：key = `${type}:${id}`，value = 文本名称
  const [resourceNameMap, setResourceNameMap] = useState<Record<string, string>>({});
  // 分页状态（服务端分页）
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  // 列表筛选：用户、资源类型、资源
  const [filterUser, setFilterUser] = useState<number | undefined>(undefined);
  const [filterResourceType, setFilterResourceType] = useState<string | undefined>(undefined);
  const [filterResourceId, setFilterResourceId] = useState<number | undefined>(undefined);
  // 列表筛选下拉（资源）远程与分页
  const [filterResourceOptions, setFilterResourceOptions] = useState<{ label: string; value: number }[]>([]);
  const [fetchingFilterResources, setFetchingFilterResources] = useState(false);
  const [filterResourceSearch, setFilterResourceSearch] = useState('');
  const [filterResourcesPage, setFilterResourcesPage] = useState(1);
  const [filterResourcesHasMore, setFilterResourcesHasMore] = useState(false);
  // 来自已分配数据的筛选静态选项
  const [filterUserStaticOptions, setFilterUserStaticOptions] = useState<{ label: string; value: number }[]>([]);
  const [filterResourceStaticOptions, setFilterResourceStaticOptions] = useState<Array<{ label: string; value: number; type: string }>>([]);

  const RESOURCE_TYPE_CHOICES = [
    { value: 'warehouse', label: '仓库' },
    { value: 'project', label: '项目' },
  ];

  useEffect(() => {
    fetchAssignments(1, pageSize);
    fetchFilterUniverse();
  }, []);

  // 当分配列表加载后，尝试补全缺失的资源名称
  useEffect(() => {
    const loadMissingResourceNames = async () => {
      const tasks: Array<Promise<void>> = [];
      const seen = new Set<string>();
      assignments.forEach((a) => {
        const key = `${a.resource_type}:${a.resource_id}`;
        if (!seen.has(key)) {
          seen.add(key);
          if (!resourceNameMap[key]) {
            tasks.push(fetchResourceName(a.resource_type, a.resource_id).then((label) => {
              if (label) {
                setResourceNameMap((prev) => ({ ...prev, [key]: label }));
              }
            }).catch(() => { /* 忽略单条失败 */ }));
          }
        }
      });
      if (tasks.length > 0) {
        try { await Promise.all(tasks); } catch (e) { /* 忽略 */ }
      }
    };
    if (assignments && assignments.length > 0) {
      loadMissingResourceNames();
    }
  }, [assignments]);

  // 获取资源显示名称（按类型调用对应详情接口）
  const fetchResourceName = async (type: string, id: number): Promise<string | undefined> => {
    try {
      if (type === 'warehouse') {
        const res = await request.get(`/api/v1/warehouses/${id}/`);
        const w = res.data || {};
        const name = w.name || w.code || `#${id}`;
        return name;
      }
      if (type === 'project') {
        const res = await request.get(`/api/v1/project/projects/${id}/`);
        const p = res.data || {};
        const code = p.system_code || p.project_code || id;
        const name = p.name || '项目';
        return `${code} - ${name}`;
      }
    } catch (e) {
      // 静默失败
    }
    return `#${id}`;
  };

  // 基于“已分配数据矩阵”构建筛选可选项（仅出现过的用户与资源）
  const fetchFilterUniverse = async () => {
    try {
      let nextUrl: string | null = '/api/v1/permission-management/assignments/?page=1&page_size=100';
      const usersMap: Record<number, { username?: string; email?: string }> = {};
      const resourceKeys = new Set<string>();
      const resources: Array<{ type: string; id: number }> = [];
      while (nextUrl) {
        const res = await request.get(nextUrl);
        const results = res.data?.results || res.data || [];
        results.forEach((a: any) => {
          if (a && typeof a.user === 'number') {
            if (!usersMap[a.user]) usersMap[a.user] = { username: a.user_username, email: a.user_email };
          }
          if (a && a.resource_type && typeof a.resource_id === 'number') {
            const key = `${a.resource_type}:${a.resource_id}`;
            if (!resourceKeys.has(key)) {
              resourceKeys.add(key);
              resources.push({ type: a.resource_type, id: a.resource_id });
            }
          }
        });
        nextUrl = res.data?.next || null;
      }

      // 用户选项
      const userOptions = Object.entries(usersMap).map(([id, info]) => ({
        value: Number(id),
        label: `${info.username || '用户'}${info.email ? ` (${info.email})` : ''}`,
      }));
      setFilterUserStaticOptions(userOptions);

      // 资源名称预取
      const fetchNameTasks: Array<Promise<void>> = [];
      resources.forEach((r) => {
        const key = `${r.type}:${r.id}`;
        if (!resourceNameMap[key]) {
          fetchNameTasks.push(
            fetchResourceName(r.type, r.id).then((label) => {
              if (label) setResourceNameMap((prev) => ({ ...prev, [key]: label }));
            }).catch(() => {})
          );
        }
      });
      if (fetchNameTasks.length) {
        try { await Promise.all(fetchNameTasks); } catch (e) {}
      }

      // 资源选项（初次构建，名称可能随后由 resourceNameMap 更新）
      const resOptions = resources.map((r) => ({
        type: r.type,
        value: r.id,
        label: resourceNameMap[`${r.type}:${r.id}`] || `#${r.id}`,
      }));
      setFilterResourceStaticOptions(resOptions);
    } catch (e) {
      // 静默失败
    }
  };

  // 当资源名缓存更新时，刷新资源静态选项的 label
  useEffect(() => {
    if (!filterResourceStaticOptions || filterResourceStaticOptions.length === 0) return;
    const updated = filterResourceStaticOptions.map((opt) => ({
      ...opt,
      label: resourceNameMap[`${opt.type}:${opt.value}`] || opt.label,
    }));
    setFilterResourceStaticOptions(updated);
    // 仅在 resourceNameMap 或静态选项变更时运行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceNameMap]);

  const fetchAssignments = async (
    p: number = page,
    ps: number = pageSize,
    nextFilterUser: number | undefined = filterUser,
    nextFilterResourceType: string | undefined = filterResourceType,
    nextFilterResourceId: number | undefined = filterResourceId,
  ) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: p, page_size: ps };
      if (nextFilterUser !== undefined) params.user = nextFilterUser;
      if (nextFilterResourceType) params.resource_type = nextFilterResourceType;
      if (nextFilterResourceId !== undefined) params.resource_id = nextFilterResourceId;
      const response = await request.get('/api/v1/permission-management/assignments/', { params });
      const results = response.data?.results || response.data || [];
      const count = typeof response.data?.count === 'number' ? response.data.count : (Array.isArray(results) ? results.length : 0);
      setAssignments(results);
      setTotalCount(count);
      setPage(p);
      setPageSize(ps);
    } catch (error) {
      message.error('获取数据分配记录失败');
      console.error('获取数据分配记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 列表筛选：远程搜索资源（依赖 filterResourceType）
  const searchFilterResources = async (keyword: string = '', p: number = 1, append: boolean = false, rt?: string) => {
    const type = rt || filterResourceType;
    if (!type) {
      message.warning('请先选择资源类型');
      return;
    }
    setFetchingFilterResources(true);
    try {
      if (type === 'warehouse') {
        const res = await request.get('/api/v1/warehouses/', {
          params: { name: keyword || '', page: p, page_size: 10 },
        });
        const list = res.data?.results || res.data || [];
        const next = res.data?.next;
        const opts = list.map((w: any) => ({ label: `${w.name || w.code || '仓库'} (#${w.id})`, value: w.id }));
        setFilterResourceOptions((prev) => (append ? [...prev, ...opts] : opts));
        setFilterResourcesHasMore(Boolean(next) || (Array.isArray(list) && list.length === 10));
        setFilterResourcesPage(p);
      } else if (type === 'project') {
        const res = await request.get('/api/v1/project/projects/', {
          params: { search: keyword || '', page: p, page_size: 10 },
        });
        const list = res.data?.results || res.data || [];
        const next = res.data?.next;
        const opts = list.map((pr: any) => ({ label: `${pr.system_code || pr.project_code || pr.id} - ${pr.name || '项目'}`, value: pr.id }));
        setFilterResourceOptions((prev) => (append ? [...prev, ...opts] : opts));
        setFilterResourcesHasMore(Boolean(next) || (Array.isArray(list) && list.length === 10));
        setFilterResourcesPage(p);
      }
    } catch (e) {
      // 静默失败
    } finally {
      setFetchingFilterResources(false);
    }
  };

  const handleFilterResourcePopupScroll = (e: any) => {
    const target = e?.target as HTMLElement;
    if (!target) return;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
    if (nearBottom && filterResourcesHasMore && !fetchingFilterResources) {
      const nextPage = filterResourcesPage + 1;
      searchFilterResources(filterResourceSearch, nextPage, true);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = { ...values };
      if (payload.expires_at && (payload.expires_at as any).toISOString) {
        payload.expires_at = (payload.expires_at as any).toISOString();
      }
      await request.post('/api/v1/permission-management/assignments/', payload);
      message.success('数据分配成功');
      setModalVisible(false);
      await fetchAssignments(1, pageSize);
      fetchFilterUniverse();
    } catch (error) {
      message.error('数据分配失败');
      console.error('数据分配失败:', error);
    }
  };

  // 远程搜索用户
  const searchUsers = async (keyword: string = '', page: number = 1, append: boolean = false) => {
    setFetchingUsers(true);
    try {
      const res = await request.get('/api/v1/permission-management/users/', {
        params: { search: keyword, page, page_size: USERS_PAGE_SIZE },
      });
      const list = res.data?.results || res.data || [];
      const next = res.data?.next;
      const options = list.map((u: any) => ({
        label: `${u.username} (${u.email || '无邮箱'})`,
        value: u.id,
      }));
      setUserOptions((prev) => (append ? [...prev, ...options] : options));
      setUsersHasMore(Boolean(next) || (Array.isArray(list) && list.length === USERS_PAGE_SIZE));
      setUsersPage(page);
    } catch (e) {
      // 静默失败，保留现有选项
    } finally {
      setFetchingUsers(false);
    }
  };

  const handleUserPopupScroll = (e: any) => {
    const target = e?.target as HTMLElement;
    if (!target) return;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
    if (nearBottom && usersHasMore && !fetchingUsers) {
      const nextPage = usersPage + 1;
      searchUsers(userSearch, nextPage, true);
    }
  };

  // 远程搜索资源（依赖 resource_type）
  const searchResources = async (keyword: string = '', page: number = 1, append: boolean = false, rt?: string) => {
    const type = rt || form.getFieldValue('resource_type');
    if (!type) {
      message.warning('请先选择资源类型');
      return;
    }
    setFetchingResources(true);
    try {
      if (type === 'warehouse') {
        const res = await request.get('/api/v1/warehouses/', {
          params: { name: keyword || '', page, page_size: 10 },
        });
        const list = res.data?.results || res.data || [];
        const next = res.data?.next;
        const opts = list.map((w: any) => ({ label: `${w.name || w.code || '仓库'} (#${w.id})`, value: w.id }));
        setResourceOptions((prev) => (append ? [...prev, ...opts] : opts));
        setResourcesHasMore(Boolean(next) || (Array.isArray(list) && list.length === 10));
        setResourcesPage(page);
      } else if (type === 'project') {
        const res = await request.get('/api/v1/project/projects/', {
          params: { search: keyword || '', page, page_size: 10 },
        });
        const list = res.data?.results || res.data || [];
        const next = res.data?.next;
        const opts = list.map((p: any) => ({
          label: `${p.system_code || p.project_code || p.id} - ${p.name || '项目'}`,
          value: p.id,
        }));
        setResourceOptions((prev) => (append ? [...prev, ...opts] : opts));
        setResourcesHasMore(Boolean(next) || (Array.isArray(list) && list.length === 10));
        setResourcesPage(page);
      }
    } catch (e) {
      // 静默失败
    } finally {
      setFetchingResources(false);
    }
  };

  const handleResourceTypeChange = (val: string) => {
    // 清空已选资源与下拉选项，按新类型预加载一页
    setResourceOptions([]);
    form.setFieldsValue({ resource_id: undefined });
    setResourceSearch('');
    setResourcesPage(1);
    setResourcesHasMore(false);
    // 立即预加载新类型的资源列表（显式传入类型）
    searchResources('', 1, false, val);
  };

  const handleResourcePopupScroll = (e: any) => {
    const target = e?.target as HTMLElement;
    if (!target) return;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
    if (nearBottom && resourcesHasMore && !fetchingResources) {
      const nextPage = resourcesPage + 1;
      searchResources(resourceSearch, nextPage, true);
    }
  };

  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record: DataAssignment) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.user_username}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.user_email}</div>
        </div>
      ),
    },
    {
      title: '资源类型',
      dataIndex: 'resource_type_display',
      key: 'resource_type_display',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '资源',
      key: 'resource_display',
      render: (_: any, record: DataAssignment) => {
        const key = `${record.resource_type}:${record.resource_id}`;
        const label = resourceNameMap[key];
        return label || `#${record.resource_id}`;
      },
    },
    {
      title: '分配者',
      dataIndex: 'assigned_by_username',
      key: 'assigned_by_username',
    },
    {
      title: '分配时间',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date: string) => date ? new Date(date).toLocaleString() : '永不过期',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '激活' : '禁用'}
        </Tag>
      ),
    },
  ];

  // 统计数据
  const totalAssignments = assignments.length;
  const activeAssignments = assignments.filter(a => a.is_active).length;
  const warehouseAssignments = assignments.filter(a => a.resource_type === 'warehouse').length;
  const projectAssignments = assignments.filter(a => a.resource_type === 'project').length;

  return (
    <div className="data-assignments">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总分配数"
              value={totalAssignments}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="激活分配"
              value={activeAssignments}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="仓库分配"
              value={warehouseAssignments}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="项目分配"
              value={projectAssignments}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="数据分配 - 使用说明"
          description={
            <div>
              <div>为具体用户指派项目或仓库，用于“分配范围”的数据可见性。</div>
              <div>先选择用户和资源类型，再搜索选择具体资源，提交后即生效。</div>
              <div>如列表仍为空，检查该角色模板是否已将该资源的查看范围设置为“分配”。</div>
            </div>
          }
        />
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            {/* 筛选：用户 */}
            <Select
              allowClear
              showSearch
              placeholder="按用户筛选（来自已分配数据）"
              style={{ width: 240 }}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes((input || '').toLowerCase())
              }
              value={filterUser}
              onChange={(val) => { setFilterUser(val); fetchAssignments(1, pageSize, val, filterResourceType, filterResourceId); }}
              options={filterUserStaticOptions}
            />
            {/* 筛选：资源类型 */}
            <Select
              allowClear
              placeholder="按资源类型筛选"
              style={{ width: 160 }}
              value={filterResourceType}
              onChange={(val) => {
                setFilterResourceType(val);
                setFilterResourceOptions([]);
                setFilterResourceId(undefined);
                if (val) searchFilterResources('', 1, false, val);
                fetchAssignments(1, pageSize, filterUser, val, undefined);
              }}
            >
              {RESOURCE_TYPE_CHOICES.map(type => (
                <Option key={type.value} value={type.value}>{type.label}</Option>
              ))}
            </Select>
            {/* 筛选：具体资源（联动资源类型） */}
            <Select
              allowClear
              showSearch
              placeholder={filterResourceType ? '按资源筛选（来自已分配数据）' : '请先选择资源类型'}
              style={{ width: 300 }}
              disabled={!filterResourceType}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes((input || '').toLowerCase())
              }
              value={filterResourceId}
              onChange={(val) => { setFilterResourceId(val); fetchAssignments(1, pageSize, filterUser, filterResourceType, val); }}
              options={filterResourceStaticOptions
                .filter((o) => !filterResourceType || o.type === filterResourceType)
                .map((o) => ({ label: o.label, value: o.value }))}
            />
            <Button onClick={() => {
              setFilterUser(undefined);
              setFilterResourceType(undefined);
              setFilterResourceId(undefined);
              setFilterResourceOptions([]);
              fetchAssignments(1, pageSize);
            }}>重置筛选</Button>
          </Space>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增分配
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={async () => { await fetchAssignments(page, pageSize, filterUser, filterResourceType, filterResourceId); fetchFilterUniverse(); }}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={assignments}
          loading={loading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (p, ps) => {
              fetchAssignments(p, ps || pageSize);
            },
            onShowSizeChange: (_current, size) => {
              if (size) fetchAssignments(1, size);
            },
          }}
        />
      </Card>

      <Modal
        title="新增数据分配"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="user"
            label="用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              showSearch
              placeholder="搜索用户名/邮箱"
              filterOption={false}
              onSearch={(v) => { setUserSearch(v); searchUsers(v, 1, false); }}
              onFocus={() => { setUserSearch(''); searchUsers('', 1, false); }}
              notFoundContent={fetchingUsers ? '加载中...' : '无匹配用户'}
              options={userOptions}
              onPopupScroll={handleUserPopupScroll}
            />
          </Form.Item>

          <Form.Item
            name="resource_type"
            label="资源类型"
            rules={[{ required: true, message: '请选择资源类型' }]}
          >
            <Select placeholder="请选择资源类型" onChange={handleResourceTypeChange}>
              {RESOURCE_TYPE_CHOICES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="resource_id"
            label="资源"
            rules={[{ required: true, message: '请选择资源' }]}
          >
            <Select
              showSearch
              placeholder="搜索资源名称/编码"
              filterOption={false}
              onSearch={(v) => { setResourceSearch(v); searchResources(v, 1, false); }}
              onFocus={() => { setResourceSearch(''); searchResources('', 1, false); }}
              notFoundContent={fetchingResources ? '加载中...' : '无匹配资源'}
              options={resourceOptions}
              onPopupScroll={handleResourcePopupScroll}
            />
          </Form.Item>

          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker 
              showTime 
              placeholder="选择过期时间（可选）"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DataAssignments;
